import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import api from "../../../utils/api";

const DISPLAY_STATUSES = ["all", "pending", "accept", "delivery pickup", "out for delivery", "completed"];

const STATUS_FLOW = {
    pending: ["accept", "reject"],
    accept: ["delivery pickup"],
    "delivery pickup": ["out for delivery"],
    "out for delivery": ["completed"],
    completed: [],
};

const useOnlineOrders = () => {
    // ─── Orders & search ──────────────────────────────────────────────────────
    const [orders, setOrders] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSort, setSelectedSort] = useState("");

    // ─── Selected order (detail view) ─────────────────────────────────────────
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const [statusHistory, setStatusHistory] = useState([]);
    const [rows, setRows] = useState([]);
    const [showSubmitButton, setShowSubmitButton] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [selectedReasons, setSelectedReasons] = useState({});
    const [reasons, setReasons] = useState([]);

    // ─── Popup order ──────────────────────────────────────────────────────────
    const [popupOrder, setPopupOrder] = useState(null);
    const [isRejecting, setIsRejecting] = useState(false);
    const [selectedRejectReason, setSelectedRejectReason] = useState("");

    // ─── Address ──────────────────────────────────────────────────────────────
    const [address, setAddress] = useState("");
    const [addressOptions, setAddressOptions] = useState([]);

    // ─── Driver ───────────────────────────────────────────────────────────────
    const [driverList, setDriverList] = useState([]);
    const [driverId, setDriverId] = useState("");
    const [driverName, setDriverName] = useState("");
    const [driverMobile, setDriverMobile] = useState("");
    const [useNewDriver, setUseNewDriver] = useState(false);

    const SOCKET_URL = process.env.REACT_APP_API_BASE;
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(null);


    // ─── Fetch ────────────────────────────────────────────────────────────────

    useEffect(() => { fetchDrivers(); }, []);
    useEffect(() => { fetchRejectReasons(); }, []);

    const fetchRejectReasons = async () => {
        try {
            const res = await api.get(`/settings/reject-reasons`);
            const list = Array.isArray(res.data?.reasons) ? res.data.reasons : [];
            setReasons(list);
        } catch (error) {
            setReasons([]);
        }
    };

    const fetchDrivers = async () => {
        try {
            const response = await api.get(`/orders/drivers`);
            setDriverList(response.data);
        } catch (error) {
            console.error("Driver fetch error:", error);
        }
    };

    useEffect(() => { fetchOrders(); }, []);

    const fetchOrders = async () => {
        try {
            const response = await api.get(`/orders/all`);
            setOrders(response.data);
        } catch (error) {
            console.error("Error fetching orders:", error);
        }
    };

    // ─── Socket ───────────────────────────────────────────────────────────────

    useEffect(() => {
        const socket = io(SOCKET_URL);
        socket.on("new-order", (order) => {
            setOrders(prev => [order, ...prev]);
        });
        return () => socket.disconnect();
    }, []);

    // ─── Address fetching ─────────────────────────────────────────────────────

    const loadAddressOptions = async (phoneNumber, currentAddress) => {
        if (!phoneNumber) {
            setAddress("");
            setAddressOptions([]);
            return;
        }
        try {
            const response = await api.get(`/orders/by-phone/${phoneNumber}`);
            const unique = [...new Set((response.data || []).map(o => o.address).filter(Boolean))];
            setAddressOptions(unique);
            if (currentAddress && unique.includes(currentAddress)) {
                setAddress(currentAddress);
            } else {
                setAddress(unique[0] || "");
            }
        } catch {
            setAddress("");
            setAddressOptions([]);
        }
    };

    useEffect(() => {
        loadAddressOptions(popupOrder?.phoneNumber, popupOrder?.address);
    }, [popupOrder]);

    useEffect(() => {
        loadAddressOptions(selectedOrder?.phoneNumber, selectedOrder?.address);
    }, [selectedOrder]);

    useEffect(() => {
        if (popupOrder?.products) {
            setRows(popupOrder.products.filter(p => p.active !== false));
        }
    }, [popupOrder]);

    useEffect(() => {
        if (selectedOrder) {
            fetchStatusHistory(selectedOrder._id);
            setAddress(selectedOrder.address || "");
            fetchDrivers();
            setRows((selectedOrder.products || []).filter(p => p.active !== false));
        } else {
            setRows([]);
        }
    }, [selectedOrder]);

    const fetchStatusHistory = async (orderId) => {
        try {
            const response = await api.get(`/orders/statusHistory/${orderId}`);
            setStatusHistory(response.data);
        } catch (error) {
            console.error("Error fetching status history:", error);
        }
    };

    // ─── Status helpers ───────────────────────────────────────────────────────

    const handleStatusClick = (status) => {
        // "all" always resets; any other status just sets it (no toggle)
        // This prevents double-click from accidentally resetting back to "all"
        setSelectedSort(status === "all" ? "" : status);
    };

    const getCountByStatus = (status) => {
        if (status === "all") return searchTerm ? filteredOrders.length : orders.length;
        return orders.filter(order => order.status === status).length;
    };

    const getNextStatuses = (currentStatus) => STATUS_FLOW[currentStatus] || [];

    const getStatusColor = (status) => {
        switch (status) {
            case "pending": return "#ffc107";
            case "accept": return "#17a2b8";
            case "delivery pickup": return "#007bff";
            case "out for delivery": return "#fd7e14";
            case "completed": return "#28a745";
            case "reject": return "#dc3545";
            default: return "#6c757d";
        }
    };

    // ─── Order status updates ─────────────────────────────────────────────────

    const updatePopupOrderStatus = async (statusToUpdate) => {
        if (!popupOrder) return;
        if ((statusToUpdate === "accept" || statusToUpdate === "pending") && (!address || address.trim() === "")) {
            toast.error("Please enter a valid address.");
            return;
        }
        let finalDriverId = driverId;
        if (statusToUpdate === "delivery pickup") {
            if (!finalDriverId && (!driverName.trim() || !driverMobile.trim())) {
                toast.error("Please enter driver name and mobile.");
                return;
            }
            if (!finalDriverId && driverName.trim() && driverMobile.trim()) {
                try {
                    const driverRes = await api.post(`/orders/add`, {
                        name: driverName.trim(), mobile: driverMobile.trim()
                        , isActive: true,
                    });
                    finalDriverId = driverRes.data._id;
                } catch (err) {
                    toast.error("Error adding driver.");
                    return;
                }
            }
        }
        try {
            await api.put(`/orders/updateStatus/${popupOrder.orderId}`, {
                status: statusToUpdate,
                address: address || null,
                assignedDriver: finalDriverId || popupOrder.assignedDriver || null,
                driverName: driverName || null,
                driverMobile: driverMobile || null,
            });
            const updatedOrder = {
                ...popupOrder, status: statusToUpdate,
                assignedDriver: finalDriverId || popupOrder.assignedDriver || null,
                driverName, driverMobile,
            };
            setPopupOrder(updatedOrder);
            if (finalDriverId) setDriverId(finalDriverId);
            else if (updatedOrder.assignedDriver) setDriverId(updatedOrder.assignedDriver);
            toast.success("Status updated successfully!");
            fetchOrders();
            setPopupOrder(null);
        } catch (error) {
            toast.error("Failed to update order status.");
        }
    };

    const updateOrderStatus = async (statusToUpdate, rejectReason = "") => {
        if (!selectedOrder) return;
        if (statusToUpdate === "accept" && (!address || address.trim() === "")) {
            toast.error("Cannot accept order without an address.");
            return;
        }
        if (statusToUpdate === "reject" && !rejectReason) {
            toast.error("Please select a rejection reason.");
            return;
        }
        let finalDriverId = driverId;
        if (statusToUpdate === "delivery pickup") {
            if (!driverId && (!driverName.trim() || !driverMobile.trim())) {
                toast.warn("Please enter driver name and mobile number.");
                return;
            }
            if (!finalDriverId && driverName.trim() && driverMobile.trim()) {
                try {
                    const driverRes = await api.post(`/orders/add`, {
                        name: driverName.trim(), mobile: driverMobile.trim(), isActive: true,
                    });
                    finalDriverId = driverRes.data._id;
                } catch (err) {
                    toast.error("Error adding driver.");
                    return;
                }
            }
        }
        try {
            const payload = {
                status: statusToUpdate,
                address: address || null,
                assignedDriver: finalDriverId || selectedOrder.assignedDriver || null,
            };
            if (statusToUpdate === "reject") payload.rejectReason = rejectReason;

            await api.put(`/orders/updateStatus/${selectedOrder.orderId}`, payload);
            toast.success("Order status updated successfully!");
            fetchOrders();
            setSelectedOrder(prev => ({
                ...prev, status: statusToUpdate,
                assignedDriver: finalDriverId || prev.assignedDriver || null,
                ...(statusToUpdate === "reject" ? { rejectReason } : {}),
            }));
            if (finalDriverId) setDriverId(finalDriverId);
        } catch (error) {
            toast.error("Failed to update order status.");
        }
    };

    const updatePopupOrderStatusReject = async (status, reason) => {
        if (!popupOrder) return;
        try {
            const payload = { status, rejectReason: reason, orderId: popupOrder.orderId };
            if (address) payload.address = address;
            await api.put(`/orders/updateStatus/${popupOrder.orderId}`, payload);
            toast.success("Order status updated!");
            setPopupOrder(prev => ({
                ...prev,
                status: "reject",
                rejectReason: reason
            }));

            setIsRejecting(false);
            setPopupOrder(null);
            fetchOrders();
        } catch (error) {
            toast.error("Failed to update status.");
        }
    };

    // ─── Product actions ──────────────────────────────────────────────────────

    const handleSelectSuggestion = async (index, product) => {
        try {
            const res = await api.get(`/products/${product._id}`);
            const latestProduct = res.data;
            const updated = [...rows];
            updated[index].productTitle = latestProduct.productName;
            updated[index].price = latestProduct.offerPrice || latestProduct.price;
            updated[index].packageCharge = latestProduct.packageCharge || 0;
            updated[index].productId = latestProduct._id;
            setRows(updated);
            setSuggestions([]);
        } catch (err) {
            console.error("Error fetching latest product price", err);
        }
    };

    const handleAddRow = () => {
        setRows(prev => [...prev, {
            _id: `new-${Date.now()}`, productTitle: "", quantity: 1, price: 0, isNew: true, productId: null,
        }]);
        setShowSubmitButton(true);
    };

    const handleChange = async (index, field, value) => {
        const updated = [...rows];
        updated[index][field] = value;
        setRows(updated);
        if (field === "productTitle" && value.length > 2) {
            try {
                const res = await api.get(`/products/search?query=${value}`);
                setSuggestions(res.data);
            } catch (err) {
                console.error("Error fetching suggestions", err);
            }
        } else {
            setSuggestions([]);
        }
    };

    const handleSubmit = async () => {
        const newItems = rows.filter(row => row.isNew);
        const cleanedItems = newItems.map((item) => {
            if (!item.productId) {
                toast.error(`Product "${item.productTitle}" is not selected from suggestions. Please select a product.`);
                throw new Error("Product ID missing for new item.");
            }
            return {
                productId: item.productId, productTitle: item.productTitle,
                quantity: parseInt(item.quantity), price: parseFloat(item.price),
                subTotal: parseFloat(item.price) * parseInt(item.quantity),
                orderStatus: item.orderStatus || "",
                packageCharge: Number(item.packageCharge || 0),
                isAdmin: true,
            };
        });
        if (cleanedItems.length === 0) {
            toast.info("No new items to add.");
            setShowSubmitButton(false);
            return;
        }
        try {
            await api.put(`/orders/${selectedOrder.orderId}/admin-update`, { updatedProducts: cleanedItems });
            toast.success("Order updated successfully by admin!");
            setShowSubmitButton(false);
            fetchOrders();
            setSelectedOrder((prev) => {
                const existingProducts = [...(prev.products || [])];
                cleanedItems.forEach((item) => {
                    const idx = existingProducts.findIndex(p => (p.productId || p._id) === item.productId);
                    if (idx !== -1) {
                        const existing = existingProducts[idx];
                        const newQty = parseInt(existing.quantity) + parseInt(item.quantity);
                        existingProducts[idx] = { ...existing, quantity: newQty, price: item.price, subTotal: item.price * newQty };
                    } else {
                        existingProducts.push({ _id: item.productId, productId: item.productId, productTitle: item.productTitle, quantity: item.quantity, price: item.price, subTotal: item.subTotal, active: true });
                    }
                });
                return { ...prev, products: existingProducts };
            });
        } catch (error) {
            toast.error("Failed to update order. Please try again.");
        }
    };

    const handleProductDelete = async (index) => {
        const productToDelete = rows[index];
        const reason = selectedReasons[index];
        if (!reason) { toast.error("Please select a reason before confirming the deletion."); return; }
        if (!selectedOrder || !productToDelete || !productToDelete.productId) {
            toast.error("Cannot delete product: Order or product ID missing.");
            return;
        }
        try {
            await api.put(`/orders/${selectedOrder.orderId}/remove-product`, {
                productId: productToDelete.productId, reason,
            });
            toast.success("Product removed successfully!");
            setRows(prev => prev.filter(p => (p.productId || p._id) !== (productToDelete.productId || productToDelete._id)));
            setShowDeletePopup(false);
            setDeleteIndex(null);
            setSelectedReasons(prev => { const n = { ...prev }; delete n[index]; return n; });
            fetchOrders();
            setSelectedOrder(prev => ({
                ...prev,
                products: prev.products.filter(p => (p.productId || p._id) !== (productToDelete.productId || productToDelete._id)),
            }));
        } catch (error) {
            toast.error("Failed to remove product.");
        }
    };

    // ─── Derived data ─────────────────────────────────────────────────────────

    const filteredOrders = orders.filter((order) => {
        const matchesSearch =
            order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.phoneNumber.includes(searchTerm) ||
            order.status.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = !selectedSort || selectedSort === "all" || order.status.toLowerCase() === selectedSort.toLowerCase();
        return matchesSearch && matchesStatus;
    });

    const isFinalStagePopup = ["out for delivery", "completed"].includes(popupOrder?.status);
    const isFinalStageSelection = ["out for delivery", "completed", "reject"].includes(selectedOrder?.status);

    return {
        // Constants
        reasons,
        displayStatuses: DISPLAY_STATUSES,
        // Orders & search
        orders, searchTerm, setSearchTerm, selectedSort,
        filteredOrders,
        // Selected order
        selectedOrder, setSelectedOrder,
        newStatus, setNewStatus,
        statusHistory,
        rows, setRows,
        showSubmitButton,
        suggestions,
        deleteIndex, setDeleteIndex,
        showDeletePopup, setShowDeletePopup,
        selectedReasons, setSelectedReasons,
        isFinalStageSelection,
        // Popup order
        popupOrder, setPopupOrder,
        isRejecting, setIsRejecting,
        selectedRejectReason, setSelectedRejectReason,
        isFinalStagePopup,
        // Address
        address, setAddress,
        addressOptions,
        // Driver
        driverList,
        driverId, setDriverId,
        driverName, setDriverName,
        driverMobile, setDriverMobile,
        useNewDriver, setUseNewDriver,
        // Handlers
        handleStatusClick,
        getCountByStatus,
        getNextStatuses,
        getStatusColor,
        updatePopupOrderStatus,
        updateOrderStatus,
        updatePopupOrderStatusReject,
        handleSelectSuggestion,
        handleAddRow,
        handleChange,
        handleSubmit,
        handleProductDelete,
        activeSuggestionIndex,
        setActiveSuggestionIndex,
    };
};

export default useOnlineOrders;
